(function (global) {
  'use strict';
  const STORAGE_KEY = 'komivan.offer.v1';
  const normalizeCode = value => /^[A-Z0-9_-]{1,64}$/.test(String(value || '').trim().toUpperCase()) ? String(value).trim().toUpperCase() : null;
  const matches = (application, code, title) => [application?.code, application?.title].some(value => String(value || '').toUpperCase() === code || title && value === title);
  function allocations(cart, code, title) {
    const cartAmounts = (cart?.cart_level_discount_applications || []).filter(app => matches(app, code, title)).map(app => Number(app.total_allocated_amount || 0));
    const lineAmounts = (cart?.items || []).flatMap(item => item.line_level_discount_allocations || []).filter(row => matches(row.discount_application, code, title)).map(row => Number(row.amount || 0));
    return [...cartAmounts, ...lineAmounts].filter(value => Number.isSafeInteger(value) && value > 0).reduce((sum, value) => sum + value, 0);
  }
  function inspect(cart, code, title) {
    if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.total_price)) throw new Error('Invalid cart response');
    const saving = allocations(cart, code, title);
    const applicable = (cart.discount_codes || []).filter(row => row.applicable === true).map(row => normalizeCode(row.code)).filter(Boolean);
    const other = applicable.find(value => value !== code) || (cart.cart_level_discount_applications || []).find(app => app.type === 'discount_code' && !matches(app, code, title))?.title;
    return {applied: cart.items.length > 0 && (saving > 0 || applicable.includes(code)), saving, other: other || null, empty: cart.items.length === 0};
  }
  function estimate(cents, percentage) {
    if (!Number.isSafeInteger(cents) || cents < 0 || !Number.isFinite(percentage) || percentage <= 0 || percentage > 100) return null;
    const saving = Math.floor(cents * percentage / 100);
    return {before: cents, saving, after: cents - saving};
  }
  function createController({config, fetchCart, updateCart, storage, onState = () => {}}) {
    let selected = null, uncertain = false, state = {status: 'idle', code: null, cart: null}, pending = null;
    try { const saved = JSON.parse(storage?.getItem(STORAGE_KEY) || 'null'); if (saved?.version === config.code) { selected = normalizeCode(saved.code); uncertain = saved.uncertain === true; } } catch (_) {}
    const titleFor = code => code === config.code ? config.applicationTitle : null;
    const save = () => { try { if (selected) storage?.setItem(STORAGE_KEY, JSON.stringify({version: config.code, code: selected, uncertain})); else storage?.removeItem(STORAGE_KEY); } catch (_) {} };
    const set = (status, cart = null, detail = {}) => { uncertain = ['pending', 'unknown'].includes(status); state = {status, code: selected, cart, ...detail}; save(); onState(state); return state; };
    const verify = cart => inspect(cart, selected, titleFor(selected));
    const checked = cart => { const result = verify(cart); return result.applied ? set('applied', cart, {saving: result.saving}) : result.other ? set('conflict', cart) : set(result.empty ? 'ready' : 'error', cart); };
    async function perform(code, allowWrite) {
      const wasUncertain = uncertain;
      selected = code; save(); set('pending');
      let before;
      try { before = await fetchCart(); const result = verify(before); if (result.applied || result.other) return checked(before); }
      catch (_) { return set('unknown'); }
      if (!allowWrite) return wasUncertain ? set('unknown', before) : checked(before);
      try { return checked(await updateCart(code)); }
      catch (_) {
        try { const after = await fetchCart(); const result = verify(after); if (result.applied || result.other) return checked(after); }
        catch (_) {}
        return set('unknown');
      }
    }
    function apply(rawCode, allowWrite = true) {
      const code = normalizeCode(rawCode);
      if (!code) return Promise.resolve(set('error'));
      if (pending) return pending;
      pending = perform(code, allowWrite).finally(() => { pending = null; });
      return pending;
    }
    const initialize = rawCode => {
      if (rawCode) return apply(rawCode);
      if (selected) return apply(selected, false);
      return Promise.resolve(state);
    };
    const prepareCheckout = async () => {
      if (pending) await pending;
      if (!selected) return true;
      const result = await apply(selected, !uncertain && state.status !== 'unknown');
      return ['applied', 'conflict'].includes(result.status);
    };
    return {apply, initialize, prepareCheckout, getState: () => state, getSelected: () => selected, clear: () => { selected = null; save(); return set('idle'); }};
  }
  const pure = {STORAGE_KEY, normalizeCode, allocations, inspect, estimate, createController};
  if (typeof module !== 'undefined' && module.exports) { module.exports = pure; return; }
  const doc = global.document, configNode = doc?.querySelector('[data-keepsake-offer-config]');
  if (!configNode) return;
  let config; try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  if (config.enabled !== true || !normalizeCode(config.code)) return;
  const routeRoot = global.Shopify?.routes?.root || '/';
  if (!/^\/(?:[A-Za-z0-9_-]+\/)*$/.test(routeRoot)) return;
  let storage; try { storage = global.localStorage; } catch (_) {}
  const panels = [...doc.querySelectorAll('[data-keepsake-offer]')], words = config.words || {};
  const text = (key, values = {}) => Object.entries(values).reduce((value, [name, replacement]) => value.replaceAll('{' + name + '}', String(replacement)), words[key] || '');
  let selectedVariant = null, initialTotal = null, reloading = false;
  const isMarketEnabled = () => config.markets.includes(global.KeepsakeMarket?.getContext?.().country);
  const money = (cents, options = {}) => global.KeepsakeMarket?.formatUSDToLocal(cents, options);
  const renderMoney = (node, value) => { if (value) global.KeepsakeMarket?.renderPrice(node, value); };
  const refreshPrice = () => {
    const state = controller.getState(), available = estimate(selectedVariant?.price, config.percentage);
    doc.querySelectorAll('[data-offer-price]').forEach(node => {
      node.hidden = !isMarketEnabled() || !available || state.status === 'conflict' || state.code && state.code !== config.code;
      if (node.hidden) return;
      const options = {variantId: selectedVariant.id, sku: selectedVariant.sku};
      const formatted = money(available.saving, options);
      if (!formatted) { node.hidden = true; return; }
      node.dataset.offerPreview = 'true';
      node.querySelector('[data-offer-saving]').textContent = text('savingWithCode', {amount: formatted});
      renderMoney(node.querySelector('[data-offer-before]'), money(available.before, options));
      renderMoney(node.querySelector('[data-offer-after]'), money(available.after, options));
      node.querySelector('[data-offer-after]').setAttribute('aria-label', text('discounted') + ' ' + money(available.after, options));
    });
  };
  const onState = state => {
    panels.forEach(panel => {
      panel.hidden = !isMarketEnabled(); panel.dataset.offerState = state.status;
      const busy = state.status === 'pending';
      panel.setAttribute('aria-busy', String(busy));
      panel.querySelectorAll('input,button').forEach(node => { node.disabled = busy; });
      const input = panel.querySelector('[data-offer-code]');
      if (state.code && input && doc.activeElement !== input) input.value = state.code;
      const status = panel.querySelector('[data-offer-status]');
      const label = text(state.status, {code: state.code || config.code});
      status.textContent = state.status === 'applied' ? label + (state.saving > 0 && money(state.saving) ? ' · ' + text('save', {amount: money(state.saving)}) : '') : label;
      panel.querySelector('[data-offer-dismiss]').hidden = !['error', 'unknown', 'conflict'].includes(state.status);
    });
    refreshPrice();
    if (state.cart && Number.isSafeInteger(state.cart.total_price) && initialTotal !== null && state.cart.total_price !== initialTotal && doc.querySelector('[data-keepsake-cart]') && !reloading) {
      reloading = true; global.location.reload();
    }
    if (['applied', 'error'].includes(state.status)) global.dispatchEvent?.(new CustomEvent('keepsake:offer-result', {detail: {code: state.code, status: state.status}}));
  };
  const request = async (path, options = {}) => {
    const response = await global.fetch(routeRoot + path, {credentials: 'same-origin', cache: 'no-store', headers: {'Accept': 'application/json', 'Content-Type': 'application/json'}, signal: AbortSignal.timeout(6500), ...options});
    if (!response.ok) throw new Error('Offer request failed');
    return response.json();
  };
  const controller = createController({config, storage, onState, fetchCart: async () => { const cart = await request('cart.js'); if (initialTotal === null) initialTotal = cart.total_price; return cart; }, updateCart: code => request('cart/update.js', {method: 'POST', body: JSON.stringify({discount: code})})});
  panels.forEach(panel => {
    const input = panel.querySelector('[data-offer-code]');
    panel.querySelector('[data-offer-claim]')?.addEventListener('click', () => controller.apply(config.code));
    const apply = () => controller.apply(input.value);
    panel.querySelector('[data-offer-apply]')?.addEventListener('click', apply);
    input?.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); apply(); } });
    panel.querySelector('[data-offer-dismiss]')?.addEventListener('click', () => controller.clear());
  });
  global.addEventListener('keepsake:variant-price', event => { selectedVariant = event.detail; refreshPrice(); });
  global.addEventListener('keepsake:market-context', () => onState(controller.getState()));
  const incoming = new URLSearchParams(global.location.search).get('discount');
  const ready = isMarketEnabled() ? controller.initialize(incoming) : Promise.resolve(controller.getState());
  global.KeepsakeOffer = Object.freeze({refreshPrice, setVariant: value => {selectedVariant = value; refreshPrice();}, ready, prepareCheckout: () => isMarketEnabled() ? controller.prepareCheckout() : Promise.resolve(true), blocksCheckout: () => isMarketEnabled() && !!controller.getSelected() && !['applied', 'conflict'].includes(controller.getState().status)});
  onState(controller.getState());
})(typeof window !== 'undefined' ? window : globalThis);
